import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import {
    AcceptPermissionResponseDTO,
    BulkAcceptPermissionResponseDTO,
    GrantPermissionResponseDTO,
    RemovePermissionResponseDTO,
    AlmostThereResponse,
} from 'src/common/dtos/access-permissions.dto';
import { environment } from 'src/environments/environment';

import {
    GranteeDetailsInterface,
    GrantorDetailsInterface,
} from '../../../common/interfaces/access-permissions.interface';
import { RegulatoryQuestionsInterface } from '../../../common/interfaces/regulatory-questions.interface';
import { LoggerService } from '../logger/logger.service';
import commonUtils from '../../../common/utils/common';
import { PERMISSION_REQUEST_ERRORS } from 'src/app/enums/permission_request_error.enum';
import { ClientTypes } from 'src/common/enums/client-types.enum';

@Injectable({
    providedIn: 'root',
})
export class AccessPermissionsService {
    private readonly httpClient = inject(HttpClient);
    private readonly logger = inject(LoggerService);


    /**
     * Grants permission to a grantee using the MVP endpoint.
     * 
     * @param granteeDetails - Information about the person receiving access
     * @param grantorDetails - Information about the person granting access
     * @param accountName - The name of the account for which access is being granted
     * @returns Observable<GrantPermissionResponseDTO> - Response containing the result of the grant operation
     * 
     */
    grantPermissionMvp(
        granteeDetails: GranteeDetailsInterface,
        grantorDetails: GrantorDetailsInterface,
        accountName: string,
    ): Observable<GrantPermissionResponseDTO> {
        return this.httpClient.post<GrantPermissionResponseDTO>(
            `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.GRANT_PERMISSION_MVP}`,
            {
                granteeDetails,
                grantorDetails,
                accountName,
            },
        );
    }

    /**
     * Removes previously granted permission from a grantee.
     * 
     * @param granteeDetails - Information about the person whose access is being removed
     * @param grantorDetails - Information about the person removing access
     * @returns Observable<RemovePermissionResponseDTO> - Response confirming the removal operation
     */
    removePermission(
        granteeDetails: GranteeDetailsInterface,
        grantorDetails: GrantorDetailsInterface,
    ): Observable<RemovePermissionResponseDTO> {
        return this.httpClient.post<GrantPermissionResponseDTO>(
            `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.REMOVE_PERMISSION}`,
            {
                granteeDetails,
                grantorDetails,
            },
        );
    }

    /**
     * Accepts a permission request using a token, optionally including regulatory questions.
     *
     * @param token - The unique token associated with the permission request
     * @param granteeRegulatoryQuestions - Optional regulatory questions that must be answered for limited access
     * @returns Observable<AcceptPermissionResponseDTO> - Response indicating acceptance status
     *
     * @remarks
     * This API is called by the grantee to accept access to a grantor's account.
     * For limited access, regulatory questions must be provided.
     */
    acceptPermission(
        token: string,
        granteeRegulatoryQuestions?: RegulatoryQuestionsInterface,
    ): Observable<AcceptPermissionResponseDTO> {
        return this.httpClient.post<any>(
            `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.ACCEPT_PERMISSION}`,
            {
                token,
                granteeRegulatoryQuestions,
            },
        );
    }

    /**
     * Accepts a bulk permission request using a token, optionally including regulatory questions.
     *
     * @param token - The unique token associated with the permission request
     * @param granteeRegulatoryQuestions - Optional regulatory questions that must be answered for limited access
     * @returns Observable<BulkAcceptPermissionResponseDTO> - Response containing acceptance results for each account
     *
     * @remarks
     * This API is called by the grantee to bulk accept access to a grantor's accounts.
     * For limited access, regulatory questions must be provided.
     * The response contains a `results` array with the outcome for each account.
     */
    bulkAcceptPermission(
        token: string,
        granteeRegulatoryQuestions?: RegulatoryQuestionsInterface,
    ): Observable<BulkAcceptPermissionResponseDTO> {
        return this.httpClient.post<BulkAcceptPermissionResponseDTO>(
            `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.BULK_ACCEPT_PERMISSION}`,
            {
                token,
                granteeRegulatoryQuestions,
            },
        );
    }

    /**
     * Marks a grantee as having completed lite registration.
     * 
     * @param token - The unique token associated with the permission request
     * @returns Observable<AlmostThereResponse> - Response containing client type and email status
     * 
     * @remarks
     * This API is used in the "Almost There" flow for non-Vanguard clients (NVC).
     * It validates the token and updates the grantee record to track lite registration progress.
     * Returns an error response if token validation fails or the record update fails.
     */
    markGranteeAsLiteRegistered(token: string): Observable<AlmostThereResponse> {
        const tokenValidationResult = commonUtils.isTokenValid(token);
        // NOTE: return to show TECH_DIFF error page
        const invalidOrMissingDataResponse = {
            error: PERMISSION_REQUEST_ERRORS.RECORD_NOT_FOUND,
            clientType: ClientTypes.UNKNOWN,
        };

        if (!tokenValidationResult.valid) {
            this.logger.error(tokenValidationResult.errors);
            return of(invalidOrMissingDataResponse);
        }
        return this.httpClient
            .post<AlmostThereResponse>(
                `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.PUBLIC_LITE_REGISTERED_USERS_UPDATE}`,
                {
                    token,
                },
            )
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    const message =
                        error.error?.message ||
                        `${AccessPermissionsService.name}: Failed to update grantee record after lite registration`;
                    this.logger.error(message);
                    return of(invalidOrMissingDataResponse);
                }),
            );
    }
}
